/**
 * SensorIntegrity - Sensor integrity hash for backend validation
 *
 * Computes the sensor hash at runtime (compiled script same-origin fetch or build-injected
 * constant). The hash is sent on every request in the X-Sensor-Integrity header so the
 * backend can verify the sensor has not been tampered with.
 */

import HashCalculator from '../integrity/hash-calculator.js';

export class SensorIntegrity {
	constructor(config, logger) {
		this.config = config;
		this.logger = logger;
		this.hashCalculator = new HashCalculator();
		this._cachedHash = null;
	}

	/**
	 * Returns the sensor integrity hash.
	 * Priority: 1) build-injected constant (matches build-time hash in sensor-hashes.json),
	 * @returns {Promise<string|null>}
	 */
	async getSensorHash() {
		if (this._cachedHash !== null) {
			return this._cachedHash;
		}

		const embedded = typeof globalThis !== 'undefined' && (globalThis.SENSOR_INTEGRITY_HASH || globalThis.SENSOR_SOURCE_HASH);
		if (embedded && typeof embedded === 'string' && embedded.length === 64 && /^[a-f0-9]+$/i.test(embedded)) {
			this._cachedHash = embedded;
			this.logger.debug('Sensor integrity hash from embedded constant');
			return embedded;
		}

		try {
			const scriptUrl = this._findSensorScriptUrl();
			if (scriptUrl && this.hashCalculator.isSameOrigin(scriptUrl)) {
				const hash = await this.hashCalculator.sha256HexFromUrlSameOrigin(scriptUrl);
				if (hash) {
					this._cachedHash = hash;
					this.logger.debug('Sensor integrity hash calculated from compiled script');
					return hash;
				}
			}
		} catch (e) {
			this.logger.debug('Sensor hash from script failed', e);
		}

		return null;
	}

	_findSensorScriptUrl() {
		if (typeof document === 'undefined') return null;
		const scripts = document.getElementsByTagName('script');
		for (let i = scripts.length - 1; i >= 0; i--) {
			const src = scripts[i] && scripts[i].src;
			if (src && (src.indexOf('adr-sensor') !== -1 || src.indexOf('iris-adr') !== -1)) {
				return src;
			}
		}
		if (document.currentScript && document.currentScript.src) {
			return document.currentScript.src;
		}
		return null;
	}
}

export default SensorIntegrity;
